import PromiseWorker from 'promise-worker';
import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  InterleavedBufferAttribute,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  Object3D,
} from 'three';

import { isShownBelow } from '../../../utils/three/visibility';
import { ShapeEdgesWorker } from '../../workers/worker-imports';
import { computeShapeEdges } from './compute-shape-edges';

/** The positions and triangles of geometries, whose shape edges the worker finds. */
export type ShapeEdgesRequest = { positions: Float32Array; index?: Uint32Array }[];

let worker: PromiseWorker | undefined;

/**
 * Gets the x, y and z of the vertices of the geometry, in an array of their own.
 */
function getPositions(geometry: BufferGeometry): Float32Array {
  const attribute = geometry.getAttribute('position');

  if (
    !(attribute instanceof InterleavedBufferAttribute) &&
    attribute.array instanceof Float32Array &&
    attribute.itemSize === 3 &&
    !attribute.normalized
  ) {
    return attribute.array.slice(0, attribute.count * 3);
  }

  const positions = new Float32Array(attribute.count * 3);
  for (let i = 0; i < attribute.count; i++) {
    positions[i * 3] = attribute.getX(i);
    positions[i * 3 + 1] = attribute.getY(i);
    positions[i * 3 + 2] = attribute.getZ(i);
  }

  return positions;
}

/**
 * Gets the shape edges of the geometries, see computeShapeEdges, in a worker so that the page goes
 * on meanwhile. Right away where there are no workers, as in tests.
 */
export async function findShapeEdges(geometries: BufferGeometry[]): Promise<Float32Array[]> {
  // In arrays of their own: those of a glb file are views of the whole file, which would otherwise
  // be copied to the worker with each of them.
  const request: ShapeEdgesRequest = geometries.map((geometry) => ({
    positions: getPositions(geometry),
    index: geometry.index ? Uint32Array.from(geometry.index.array) : undefined,
  }));

  if (typeof Worker === 'undefined') {
    return request.map((x) => computeShapeEdges(x.positions, x.index));
  }

  worker ??= new PromiseWorker(new ShapeEdgesWorker());
  return worker.postMessage<Float32Array[], ShapeEdgesRequest>(request);
}

const color = new Color(0, 1, 0);

/**
 * The edges of the shapes of the meshes of an asset, shown while it is selected: where their
 * surfaces bend, without the edges between the triangles of flat and curved surfaces that a
 * wireframe has. They are found once per geometry, in a worker, and drawn as one object, of the
 * meshes that are shown, which is drawn again as layers are shown and hidden.
 */
export class ShapeEdges {
  /** The edges of each geometry of the meshes, in its coordinates. */
  private edges?: Promise<Map<BufferGeometry, Float32Array>>;
  private lines?: LineSegments;
  /** Counts the calls to show and hide, so that a show that waited for the edges gives way. */
  private calls = 0;

  /**
   * @param owner The asset, which the edges are drawn in.
   * @param meshes Its meshes that have edges, not e.g. its texts.
   */
  constructor(
    private owner: Object3D,
    private meshes: Mesh[],
  ) {}

  /**
   * Shows the edges of the meshes that are shown, once they have been found the first time.
   */
  async show() {
    const call = ++this.calls;

    try {
      this.edges ??= this.find();
      const edges = await this.edges;

      if (call === this.calls) {
        this.removeLines();
        this.addLines(edges);
      }
    } catch {
      // Found again the next time. They only show what is selected.
      this.edges = undefined;
    }
  }

  hide() {
    this.calls++;
    this.removeLines();
  }

  /**
   * Whether the object is the lines of the edges, which other lines of the asset are not.
   */
  owns(object: Object3D) {
    return object === this.lines;
  }

  private async find() {
    const geometries = [...new Set(this.meshes.map((x) => x.geometry))];
    const edges = await findShapeEdges(geometries);
    return new Map(geometries.map((geometry, i) => [geometry, edges[i]]));
  }

  private addLines(edges: Map<BufferGeometry, Float32Array>) {
    this.owner.updateMatrixWorld(true);
    const toOwner = this.owner.matrixWorld.clone().invert();
    const shown = this.meshes.filter((mesh) => isShownBelow(mesh, this.owner));
    const positions = new Float32Array(
      shown.reduce((sum, mesh) => sum + (edges.get(mesh.geometry)?.length ?? 0), 0),
    );

    const matrix = new Matrix4();
    let offset = 0;
    for (const mesh of shown) {
      const points = edges.get(mesh.geometry);
      if (!points) {
        continue;
      }

      const e = matrix.multiplyMatrices(toOwner, mesh.matrixWorld).elements;
      for (let i = 0; i < points.length; i += 3) {
        const x = points[i];
        const y = points[i + 1];
        const z = points[i + 2];
        positions[offset++] = e[0] * x + e[4] * y + e[8] * z + e[12];
        positions[offset++] = e[1] * x + e[5] * y + e[9] * z + e[13];
        positions[offset++] = e[2] * x + e[6] * y + e[10] * z + e[14];
      }
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    this.lines = new LineSegments(geometry, new LineBasicMaterial({ color }));
    // Only shows what is selected: it can't be picked or snapped to.
    this.lines.raycast = () => undefined;
    this.owner.add(this.lines);
  }

  private removeLines() {
    if (this.lines) {
      this.lines.removeFromParent();
      this.lines.geometry.dispose();
      (this.lines.material as LineBasicMaterial).dispose();
      this.lines = undefined;
    }
  }
}
